import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Tool, ToolVersion, useToolSpecProviderQuery } from "graphql/_Types";
import { ToolSpec } from "components/ToolParamsGrid/ToolParamsGrid.types";
import { CallOutError } from "components/CallOutError/CallOutError";
import { ContextOutOfBoundsError } from "providers/ContextOutOfBoundsError";
import { captureException } from "@sentry/react";
import { useTenantContext } from "providers/TenantProvider/TenantProvider";
import axios from "axios";
import { getEnvVar } from "ideas.env";
import AppLoading from "components/AppLoading/AppLoading";
import { ToolMaturity } from "types/constants";

type ToolSpecProviderContextValue = {
  availableToolSpecs: ToolSpecAndToolVersionId[];
  docsMap: DocsMap;
};

export const ToolSpecProviderContext = createContext<
  ToolSpecProviderContextValue | undefined
>(undefined);

export const useToolSpecContext = () => {
  const context = useContext(ToolSpecProviderContext);
  if (context === undefined) {
    throw new ContextOutOfBoundsError("UserProviderContext");
  }

  return context;
};

/**
 * Type guard to check if a tool version is a version of an "analysis" tool.
 * "Analysis" tools are executed from analysis tables whereas "utility" tools
 * (e.g. bundle tool, export to dandi, etc) are run outside of analysis tables.
 */
const isAnalysisToolVersion = <T extends Pick<ToolVersion, "toolSpec">>(
  toolVersion: T,
): toolVersion is T & { toolSpec: ToolSpec } => {
  const toolSpec = toolVersion.toolSpec as ToolSpec | null;
  const isUtilityToolVersion = toolSpec?.toolkit === "System Utilities";
  return toolSpec !== null && !isUtilityToolVersion;
};

interface ToolSpecProviderProps {
  children: React.ReactNode;
}

export type ToolSpecAndToolVersionId = {
  spec: ToolSpec;
  toolId: Tool["id"];
  toolVersionId: ToolVersion["id"];
  maturity: ToolVersion["maturity"];
  credits: ToolVersion["credits"];
  versionCreated: ToolVersion["created"];
  toolCreated: Tool["created"];
};

type DocsMap = {
  [key: Tool["key"]]:
    | {
        default: string;
        [version: ToolVersion["version"]]: string | undefined;
      }
    | undefined;
};

export const ToolSpecProvider = ({ children }: ToolSpecProviderProps) => {
  const currentTenant = useTenantContext((s) => s.currentTenant);
  const { data, error, loading } = useToolSpecProviderQuery({
    variables: {
      tenantId: currentTenant.id,
      deprecatedMaturity: ToolMaturity.DEPRECATED,
    },
    onError: (err) => captureException(err),
  });

  const [docs, setDocs] = useState<DocsMap | "loading" | "error">("loading");

  useEffect(() => {
    axios
      .get<DocsMap>(getEnvVar("URL_DOCS_MAPPER"))
      .then((res) => {
        setDocs(res.data);
      })
      .catch(() => {
        captureException("Failed to fetch tool docs");
        setDocs("error");
      });
  }, []);

  const availableToolSpecs = useMemo(() => {
    return data?.tools?.nodes
      .flatMap((tool) =>
        tool.toolVersions.nodes.map((toolVersion) => ({
          ...toolVersion,
          toolCreated: tool.created,
        })),
      )
      .filter(isAnalysisToolVersion)
      .map((toolVersion) => ({
        toolId: toolVersion.toolId,
        toolVersionId: toolVersion.id,
        spec: toolVersion.toolSpec,
        maturity: toolVersion.maturity,
        credits: toolVersion.credits,
        versionCreated: toolVersion.created,
        toolCreated: toolVersion.toolCreated,
      }));
  }, [data]);

  if (loading || docs === "loading") {
    return <AppLoading />;
  }

  if (error || availableToolSpecs === undefined) {
    return <CallOutError />;
  }

  return (
    <ToolSpecProviderContext.Provider
      value={{
        availableToolSpecs,
        // Don't want to fail to load the entire app if we fail to fetch docs links
        docsMap: docs !== "error" ? docs : ({} as DocsMap),
      }}
    >
      {children}
    </ToolSpecProviderContext.Provider>
  );
};
